import React from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import NextLink from 'next/link';
import { useRouter } from 'next/router';

// ---------------------------------------------------------

const Link = (props) => {
  const router = useRouter();
  let {
    activeClassName,
    ariaLabel,
    children,
    className,
    onClick,
    url,
    query = {},
    forceAnchorTag,
    dataLayerEvent
  } = props;

  if (!url) return null;

  // ---------------------------------------------------------

  const externalUrl = url.match(/^http/);
  const sibcyClineUrl = url.match(/^https:\/\/www.sibcycline.com/);
  const sibcyClinePathSplit = url.split('.com');

  // ---------------------------------------------------------

  const classes = classNames({
    [activeClassName]: !externalUrl && (router.pathname === url || router.asPath === url),
    [className]: className
  });

  // ---

  const handleOnClick = () => {
    if (dataLayerEvent) {
      window.dataLayer.push({
        event: dataLayerEvent
      });
    }
    if (onClick) {
      onClick();
    }
  };

  // ---------------------------------------------------------

  if (sibcyClineUrl) {
    return (
      <NextLink
        className={classes}
        href={sibcyClinePathSplit[1]}
        aria-label={ariaLabel}
        onClick={handleOnClick}
      >
        {children}
      </NextLink>
    );
  }

  if (externalUrl && !sibcyClineUrl) {
    return (
      <a
        href={url}
        className={classes}
        aria-label={ariaLabel}
        target="_blank"
        rel="noreferrer"
        onClick={handleOnClick}
      >
        {children}
      </a>
    );
  }

  if (forceAnchorTag) {
    return (
      <a href={url} className={classes} aria-label={ariaLabel} onClick={handleOnClick}>
        {children}
      </a>
    );
  }

  // When there's no separate `query` object, pass `url` straight through as a
  // string so Next.js parses any query string it already contains (e.g.
  // "/community/x?tab=local-events"). Building { pathname: url, query } here would
  // treat a "?" inside `url` as literal pathname content and percent-encode it
  // (producing a broken "%3Fparam=" href) instead of a query separator.
  const hasQueryObject = Object.keys(query).length > 0;

  return (
    <NextLink
      href={hasQueryObject ? { pathname: url, query } : url}
      className={classes}
      aria-label={ariaLabel}
      onClick={handleOnClick}
    >
      {children}
    </NextLink>
  );
};

Link.propTypes = {
  /**
   * Specifies text that labels the current element, when a text label is not visible on the screen.
   */
  ariaLabel: PropTypes.string,

  /**
   * Specifies what is rendered inside the link.
   */
  children: PropTypes.oneOfType([PropTypes.string, PropTypes.node]),

  /**
   * Used when the Link should not be using next link, for an internal URL
   */
  forceAnchorTag: PropTypes.bool,

  /**
   * Click handler function to execute when the link is clicked
   */
  onClick: PropTypes.func,

  /**
   * Specifies any query parameters to be added to the page where the url links
   * Will be ignored on external links
   */
  query: PropTypes.object,

  /**
   * Specifies where the url links
   * (external links that start with http will open in new window).
   */
  url: PropTypes.string.isRequired,

  dataLayerEvent: PropTypes.string
};

export default Link;
